import { composeGetTranslation } from '../../../../../../../common/react/utils/translations';
import { STATS_LEADERS_CATEGORY } from './categories';

export const FIRST_SORTABLE_COLUMN_INDEX = 4;

export const STAT_IDS = {
    AFL_FANTASY_POINTS: 'dreamTeamPoints',
    GOALS: 'goals',
    GOAL_ASSIST: 'goalAssists',
    GOAL_ACCURACY: 'goalAccuracy',
    SHOTS_AT_GOAL: 'shotsAtGoal',
    BEHINDS: 'behinds',
    DISPOSALS: 'disposals',
    MARKS: 'marks',
    CONTESTED_MARKS: 'contestedMarks',
    MARKS_INSIDE_50: 'marksInside50',
    MARKS_ON_LEAD: 'marksOnLead',
    INTERCEPT_MARKS: 'interceptMarks',
    TACKLES: 'tackles',
    TACKLES_INSIDE_50: 'tacklesInside50',
    PRESSURE_ACTS: 'pressureActs',
    DEFENSIVE_HALF_PRESSURE_ACTS: 'defHalfPressureActs',
    CONTESTED_DEFENSIVE_ONE_ON_ONES: 'contestDefOneOnOnes',
    CONTESTED_DEFENSIVE_LOSSES: 'contestDefLosses',
    CONTESTED_DEFENSIVE_LOSS_PERCENT: 'contestDefLossPercentage',
    CLEARANCES: 'totalClearances',
    KICKS: 'kicks',
    HANDBALLS: 'handballs',
    HIT_OUTS: 'hitouts',
    FREES_FOR: 'freesFor',
    FREES_AGAINST: 'freesAgainst',
    BOUNCES: 'bounces',
    ONE_PERCENTERS: 'onePercenters',
    TIME_ON_GROUND: 'timeOnGroundPercentage',
    INSIDE_50S: 'inside50s',
    DISPOSAL_EFFICIENCY: 'disposalEfficiency',
    CLANGERS: 'clangers',
    REBOUND_50S: 'rebound50s',
    EFFECTIVE_KICKS: 'effectiveKicks',
    KICK_EFFICIENCY: 'kickEfficiency',
    KICK_TO_HANDBALL_RATIO: 'kickToHandballRatio',
    EFFECTIVE_DISPOSALS: 'effectiveDisposals',
    SPOILS: 'spoils',
    CONTESTED_POSSESSIONS: 'contestedPossessions',
    UNCONTESTED_POSSESSIONS: 'uncontestedPossessions',
    INTERCEPT_POSSESSIONS: 'intercepts',
    TURNOVERS: 'turnovers',
    CONTESTED_POSSESSION_RATE: 'contestedPossessionRate',
    GROUND_BALL_GETS: 'groundBallGets',
    FWD_50_GROUND_BALL_GETS: 'f50GroundBallGets',
    CENTRE_CLEARANCES: 'centreClearances',
    STOPPAGE_CLEARANCES: 'stoppageClearances',
    TOTAL_CLEARANCES: 'totalClearances',
    HITOUTS_TO_ADVANTAGE: 'hitoutsToAdvantage',
    HITOUTS_TO_ADVANTAGE_PERCENTAGE: 'hitoutToAdvantageRate',
    HITOUT_WIN_PERCENTAGE: 'hitoutWinPercentage',
    RUCK_CONTESTS: 'ruckContests',
    SCORE_INVOLVEMENTS: 'scoreInvolvements',
    SCORE_LAUNCHES: 'scoreLaunches',
    // NOTE: The constants below are not actually used as columns but they are
    // used inside the tooltips when you hover over a table cell.
    METRES_GAINED: 'metresGained',
    CENTRE_BOUNCE_ATTENDANCES: 'centreBounceAttendances',
};

/**
 * @typedef {object} Stat
 * @property {string} id
 * @property {string} name
 * @property {string} abbreviation
 */

/**
 * @type {Object.<string, Stat>}
 */
export const STATS = {};

// eslint-disable-next-line guard-for-in
for ( let key in STAT_IDS ) {
    const id = STAT_IDS[ key ];
    const getTranslation = composeGetTranslation( `label.statsLeaders.${ id }.` );

    STATS[ id ] = {
        id,
        name: getTranslation( 'name' ) ?? '',
        abbreviation: getTranslation( 'abbreviation' ) ?? ''
    };
}

Object.freeze( STATS );
export const STATS_LEADERS_COLUMNS_FOR_CATEGORY = {
    [ STATS_LEADERS_CATEGORY.KEY_STATS ]: [
        STATS[ STAT_IDS.AFL_FANTASY_POINTS ],
        STATS[ STAT_IDS.GOALS ],
        STATS[ STAT_IDS.DISPOSALS ],
        STATS[ STAT_IDS.MARKS ],
        STATS[ STAT_IDS.TACKLES ],
        STATS[ STAT_IDS.CLEARANCES ],
        STATS[ STAT_IDS.KICKS ],
        STATS[ STAT_IDS.HANDBALLS ],
        STATS[ STAT_IDS.HIT_OUTS ],
    ],
    [ STATS_LEADERS_CATEGORY.GENERAL_PLAY ]: [
        STATS[ STAT_IDS.FREES_FOR ],
        STATS[ STAT_IDS.FREES_AGAINST ],
        STATS[ STAT_IDS.AFL_FANTASY_POINTS ],
        STATS[ STAT_IDS.BOUNCES ],
        STATS[ STAT_IDS.ONE_PERCENTERS ],
        STATS[ STAT_IDS.TIME_ON_GROUND ],
    ],
    [ STATS_LEADERS_CATEGORY.DISPOSALS ]: [
        STATS[ STAT_IDS.DISPOSALS ],
        STATS[ STAT_IDS.KICKS ],
        STATS[ STAT_IDS.HANDBALLS ],
        STATS[ STAT_IDS.INSIDE_50S ],
        STATS[ STAT_IDS.DISPOSAL_EFFICIENCY ],
        STATS[ STAT_IDS.CLANGERS ],
        STATS[ STAT_IDS.REBOUND_50S ],
        STATS[ STAT_IDS.EFFECTIVE_KICKS ],
        STATS[ STAT_IDS.KICK_EFFICIENCY ],
        STATS[ STAT_IDS.KICK_TO_HANDBALL_RATIO ],
        STATS[ STAT_IDS.EFFECTIVE_DISPOSALS ],
    ],
    [ STATS_LEADERS_CATEGORY.POSSESSIONS ]: [
        STATS[ STAT_IDS.CONTESTED_POSSESSIONS ],
        STATS[ STAT_IDS.UNCONTESTED_POSSESSIONS ],
        STATS[ STAT_IDS.INTERCEPT_POSSESSIONS ],
        STATS[ STAT_IDS.TURNOVERS ],
        STATS[ STAT_IDS.CONTESTED_POSSESSION_RATE ],
        STATS[ STAT_IDS.GROUND_BALL_GETS ],
        STATS[ STAT_IDS.FWD_50_GROUND_BALL_GETS ],
    ],
    [ STATS_LEADERS_CATEGORY.STOPPAGES ]: [
        STATS[ STAT_IDS.HIT_OUTS ],
        STATS[ STAT_IDS.CENTRE_CLEARANCES ],
        STATS[ STAT_IDS.STOPPAGE_CLEARANCES ],
        STATS[ STAT_IDS.TOTAL_CLEARANCES ],
        STATS[ STAT_IDS.HITOUTS_TO_ADVANTAGE ],
        STATS[ STAT_IDS.HITOUT_WIN_PERCENTAGE ],
        STATS[ STAT_IDS.HITOUTS_TO_ADVANTAGE_PERCENTAGE ],
        STATS[ STAT_IDS.RUCK_CONTESTS ],
    ],
    [ STATS_LEADERS_CATEGORY.MARKS ]: [
        STATS[ STAT_IDS.MARKS ],
        STATS[ STAT_IDS.INSIDE_50S ],
        STATS[ STAT_IDS.CONTESTED_MARKS ],
        STATS[ STAT_IDS.MARKS_ON_LEAD ],
        STATS[ STAT_IDS.INTERCEPT_MARKS ],
    ],
    [ STATS_LEADERS_CATEGORY.SCORING ]: [
        STATS[ STAT_IDS.GOALS ],
        STATS[ STAT_IDS.BEHINDS ],
        STATS[ STAT_IDS.GOAL_ASSIST ],
        STATS[ STAT_IDS.GOAL_ACCURACY ],
        STATS[ STAT_IDS.SHOTS_AT_GOAL ],
        STATS[ STAT_IDS.SCORE_INVOLVEMENTS ],
        STATS[ STAT_IDS.SCORE_LAUNCHES ],
    ],
    [ STATS_LEADERS_CATEGORY.DEFENCE ]: [
        STATS[ STAT_IDS.TACKLES ],
        STATS[ STAT_IDS.TACKLES_INSIDE_50 ],
        STATS[ STAT_IDS.PRESSURE_ACTS ],
        STATS[ STAT_IDS.DEFENSIVE_HALF_PRESSURE_ACTS ],
        STATS[ STAT_IDS.SPOILS ],
        STATS[ STAT_IDS.CONTESTED_DEFENSIVE_ONE_ON_ONES ],
        STATS[ STAT_IDS.CONTESTED_DEFENSIVE_LOSSES ],
        STATS[ STAT_IDS.CONTESTED_DEFENSIVE_LOSS_PERCENT ],
    ],
};

Object.freeze( STATS_LEADERS_COLUMNS_FOR_CATEGORY );